import React, { useState } from 'react';
import {
  Mic, Phone, PhoneOff, CheckCircle, AlertCircle, Info,
  ChevronDown, ChevronRight, Zap, ShoppingCart, FileText,
  DollarSign, Users, BarChart3, Settings, BookOpen, Bot
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'start',
    icon: Phone,
    color: 'blue',
    title: 'Starting a Call',
    intro: 'The voice agent is available on every page via the phone button in the top bar.',
    items: [
      {
        trigger: 'Click the phone icon in the top bar',
        description: 'Opens a direct VoIP call from your browser — no app or phone number needed.',
      },
      {
        trigger: '"Call SpocoDoc"',
        description: 'Say this phrase at any time to activate the browser\'s keyword listener and start a call automatically.',
      },
    ],
    note: 'Allow microphone access when prompted. The call runs directly in your browser — nothing is installed.',
  },
  {
    id: 'basics',
    icon: Zap,
    color: 'indigo',
    title: 'What the Agent Says on Connection',
    intro: 'The agent greets you by first name and gives an immediate one-sentence workload summary:',
    bullets: [
      'Number of invoices awaiting your approval',
      'Number of expenses awaiting your approval',
      'Vendors pending approval',
      'Active subscriptions count + monthly commitment',
      'Open accruals count + total value',
      'Purchase orders in your approval queue (if PO module enabled)',
    ],
    note: 'All figures are live from the database at the moment you call.',
  },
  {
    id: 'invoices',
    icon: FileText,
    color: 'violet',
    title: 'Invoices',
    items: [
      { trigger: '"What\'s pending" / "What do I have today"', description: 'Summarises all your pending approvals.' },
      { trigger: '"Tell me about invoice [number or vendor]"', description: 'Fetches full invoice detail — vendor, amount, date, GL code, department.' },
      { trigger: '"Approve the [vendor] invoice"', description: 'Agent confirms details first, then approves on your "yes".' },
      { trigger: '"Reject the [vendor] invoice"', description: 'Agent confirms and rejects. You can optionally say a reason.' },
      { trigger: '"Put the [vendor] invoice on hold"', description: 'Marks the invoice as On Hold without rejecting it.' },
    ],
  },
  {
    id: 'expenses',
    icon: DollarSign,
    color: 'emerald',
    title: 'Expenses',
    items: [
      { trigger: '"What expenses do I need to approve?"', description: 'Lists expense claims awaiting your approval.' },
      { trigger: '"Approve [name]\'s expense"', description: 'Confirms the amount and approves.' },
      { trigger: '"Reject [name]\'s expense"', description: 'Confirms and rejects.' },
    ],
  },
  {
    id: 'vendors',
    icon: Users,
    color: 'amber',
    title: 'Vendors & Checklists',
    items: [
      {
        trigger: '"Fill in the checklist for [vendor name]"',
        description: 'Agent loads the existing checklist (or blank one), then walks through each question one by one. Say "skip" for any optional question.',
        mandatory: ['Is the vendor known to the business?', 'Is the vendor trusted?', 'Negotiator name', 'Negotiator email', 'Payment terms agreed'],
      },
      {
        trigger: '"Skip" (during checklist)',
        description: 'Skips the current question. Mandatory fields cannot be skipped — the agent will remind you.',
      },
      {
        trigger: '"Approve vendor" / "Approve it" (after checklist)',
        description: 'Approves the vendor immediately if all mandatory checklist fields are complete.',
      },
      {
        trigger: '"Approve [vendor name]"',
        description: 'Approves the vendor directly. Will fail and explain if the checklist is incomplete.',
      },
    ],
  },
  {
    id: 'subscriptions',
    icon: Settings,
    color: 'cyan',
    title: 'Subscriptions & Contracts',
    items: [
      {
        trigger: '"List my subscriptions" / "Show active contracts"',
        description: 'Returns the top 10 active subscriptions with vendor, frequency, amount, and renewal date.',
      },
      {
        trigger: '"Add a subscription for [vendor], [amount] [frequency], starting [date]"',
        description: 'Creates a new subscription/contract. Agent will confirm details before saving.',
        mandatory: ['Vendor name', 'Contract name', 'Payment frequency (Monthly / Annually)', 'Payment amount', 'Start date'],
        optional: ['End date', 'Renewal date', 'Auto-renew', 'Department', 'Notice period'],
      },
    ],
  },
  {
    id: 'accruals',
    icon: BookOpen,
    color: 'rose',
    title: 'Accruals',
    items: [
      {
        trigger: '"Show open accruals" / "List accruals"',
        description: 'Returns the top 10 active accruals with vendor, description, amount, and department.',
      },
      {
        trigger: '"Add an accrual" / "Record a cost accrual"',
        description: 'Agent collects the details and creates the accrual.',
        mandatory: ['Vendor name', 'Description', 'Estimated amount'],
        optional: ['Accrual date (defaults to today)', 'Department', 'Expected invoice date', 'Notes'],
      },
    ],
  },
  {
    id: 'po',
    icon: ShoppingCart,
    color: 'orange',
    title: 'Purchase Orders',
    moduleRequired: 'purchase_orders',
    items: [
      {
        trigger: '"Show my purchase orders" / "List POs"',
        description: 'Returns the top 10 purchase orders with status, vendor, and total.',
      },
      {
        trigger: '"Create a PO for [items and amounts]"',
        description: 'Agent collects description and line items, then creates the PO. You can choose to submit for approval immediately or save as draft.',
        mandatory: ['At least one line item (description + amount)', 'Overall PO description'],
        optional: ['Vendor', 'Department'],
      },
      {
        trigger: '"Approve that PO" / "Approve PO [number]"',
        description: 'Approves a PO in your approval queue. Advances the chain or marks Approved if you are the final approver.',
      },
    ],
  },
  {
    id: 'budgets',
    icon: BarChart3,
    color: 'teal',
    title: 'Budgets',
    items: [
      {
        trigger: '"What\'s the budget situation?" / "Budget summary"',
        description: 'Returns the overall budget summary for the current financial year — total budget, spent, committed, and remaining.',
      },
      {
        trigger: '"What\'s the [department] budget?"',
        description: 'Returns spend vs budget for a specific department, including committed costs.',
      },
      {
        trigger: '"What financial years are available?"',
        description: 'Lists all financial years set up in SpocoDoc.',
      },
    ],
  },
  {
    id: 'navigation',
    icon: Zap,
    color: 'purple',
    title: 'Navigation & Links',
    items: [
      { trigger: '"Send me the invoices link" / "Email me the dashboard"', description: 'Emails you a direct link to Invoices, Expenses, Vendors, Budgets, or Dashboard.' },
      { trigger: '"Send invoices to Teams" / "Teams link for expenses"', description: 'Posts a direct deep-link card to your Teams channel.' },
    ],
  },
  {
    id: 'memory',
    icon: Bot,
    color: 'slate',
    title: 'Agent Memory & Custom Queries',
    items: [
      {
        trigger: '"Remember to always tell me [X]" / "Add this to your queries"',
        description: 'Agent writes a SQL query and stores it. It runs automatically at the start of every future call so you always get that data.',
      },
      { trigger: '"What queries do you remember?"', description: 'Lists all stored custom queries.' },
      { trigger: '"Forget [query]" / "Delete that query"', description: 'Removes a stored query.' },
      { trigger: '"What can I do?" / "What are my permissions?"', description: 'Agent describes all your roles and what each one lets you do in SpocoDoc.' },
    ],
  },
  {
    id: 'ending',
    icon: PhoneOff,
    color: 'gray',
    title: 'Ending a Call',
    items: [
      { trigger: '"Goodbye" / "Hang up" / "That\'s all"', description: 'Agent says goodbye and disconnects gracefully.' },
      { trigger: 'Click the red hang-up button', description: 'Ends the call immediately from the browser.' },
    ],
  },
];

const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  icon: 'text-blue-600',  badge: 'bg-blue-100 text-blue-800',  dot: 'bg-blue-500' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200',icon: 'text-indigo-600',badge: 'bg-indigo-100 text-indigo-800',dot: 'bg-indigo-500' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200',icon: 'text-violet-600',badge: 'bg-violet-100 text-violet-800',dot: 'bg-violet-500' },
  emerald:{ bg: 'bg-emerald-50',border: 'border-emerald-200',icon: 'text-emerald-600',badge:'bg-emerald-100 text-emerald-800',dot:'bg-emerald-500'},
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  cyan:   { bg: 'bg-cyan-50',   border: 'border-cyan-200',  icon: 'text-cyan-600',  badge: 'bg-cyan-100 text-cyan-800',  dot: 'bg-cyan-500' },
  rose:   { bg: 'bg-rose-50',   border: 'border-rose-200',  icon: 'text-rose-600',  badge: 'bg-rose-100 text-rose-800',  dot: 'bg-rose-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200',icon: 'text-orange-600',badge: 'bg-orange-100 text-orange-800',dot:'bg-orange-500'},
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',  icon: 'text-teal-600',  badge: 'bg-teal-100 text-teal-800',  dot: 'bg-teal-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200',icon: 'text-purple-600',badge: 'bg-purple-100 text-purple-800',dot:'bg-purple-500'},
  slate:  { bg: 'bg-slate-50',  border: 'border-slate-200', icon: 'text-slate-600', badge: 'bg-slate-100 text-slate-800', dot: 'bg-slate-500' },
  gray:   { bg: 'bg-gray-50',   border: 'border-gray-200',  icon: 'text-gray-600',  badge: 'bg-gray-100 text-gray-800',  dot: 'bg-gray-400' },
};

function Section({ section, open, onToggle }) {
  const c = COLOR_MAP[section.color] || COLOR_MAP.slate;
  const Icon = section.icon;

  return (
    <div className={`rounded-xl border ${c.border} overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-5 py-4 ${c.bg} hover:brightness-95 transition-all`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-white shadow-sm ${c.icon}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-semibold text-gray-800 text-sm">{section.title}</span>
          {section.moduleRequired && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Module required</span>
          )}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {open && (
        <div className="px-5 py-4 bg-white space-y-4">
          {section.intro && <p className="text-sm text-gray-600">{section.intro}</p>}

          {section.bullets && (
            <ul className="space-y-1">
              {section.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                  {b}
                </li>
              ))}
            </ul>
          )}

          {section.items && (
            <div className="space-y-3">
              {section.items.map((item, i) => (
                <div key={i} className={`rounded-lg border ${c.border} ${c.bg} p-4`}>
                  <div className="flex items-start gap-2 mb-1">
                    <Mic className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${c.icon}`} />
                    <span className="font-mono text-sm font-semibold text-gray-800">{item.trigger}</span>
                  </div>
                  <p className="text-sm text-gray-600 ml-5">{item.description}</p>
                  {item.mandatory && (
                    <div className="ml-5 mt-2 space-y-1">
                      <p className="text-xs font-semibold text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Required fields
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {item.mandatory.map((m, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.optional && (
                    <div className="ml-5 mt-2 space-y-1">
                      <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                        <Info className="w-3 h-3" /> Optional
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {item.optional.map((o, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{o}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {section.note && (
            <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
              {section.note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VoiceAgentGuide() {
  const allIds = SECTIONS.map(s => s.id);
  const [open, setOpen] = useState(new Set(allIds));

  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const expandAll  = () => setOpen(new Set(allIds));
  const collapseAll = () => setOpen(new Set());

  return (
    <div className="p-6 max-w-3xl mx-auto">

      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-8 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-white/20 rounded-xl">
            <Mic className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-bold">Voice AI Agent Guide</h1>
        </div>
        <p className="text-blue-100 text-sm mt-1">
          Everything you can do hands-free with the SpocoDoc Finance Assistant.
          Talk naturally — the agent understands context, asks for confirmation before taking any action, and never makes up data.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          {[
            { label: 'Approve invoices & expenses', icon: CheckCircle },
            { label: 'Vendor checklists by voice', icon: Users },
            { label: 'Subscriptions & accruals', icon: Settings },
            { label: 'Purchase orders', icon: ShoppingCart },
            { label: 'Budget queries', icon: BarChart3 },
          ].map(({ label, icon: I }) => (
            <span key={label} className="flex items-center gap-1.5 text-xs bg-white/20 rounded-full px-3 py-1.5">
              <I className="w-3.5 h-3.5" /> {label}
            </span>
          ))}
        </div>
      </div>

      {/* Quick tip */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
        <div>
          <strong>Always-on safety:</strong> the agent will always confirm what it is about to do before calling any action.
          You must say "yes" (or similar) to proceed. A "no" or silence cancels the action.
        </div>
      </div>

      {/* Expand / Collapse */}
      <div className="flex gap-2 mb-4">
        <button onClick={expandAll}   className="text-xs text-blue-600 hover:underline">Expand all</button>
        <span className="text-gray-300">|</span>
        <button onClick={collapseAll} className="text-xs text-gray-500 hover:underline">Collapse all</button>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map(section => (
          <Section
            key={section.id}
            section={section}
            open={open.has(section.id)}
            onToggle={() => toggle(section.id)}
          />
        ))}
      </div>

      {/* Footer tip */}
      <div className="mt-8 text-center text-xs text-gray-400">
        The agent learns from you — say <span className="font-mono">"Remember to always tell me [X]"</span> to store a custom query that runs on every future call.
      </div>
    </div>
  );
}
